import type { ReactNode } from "react";

import { Stack } from "@mui/material";

import { useUpdateAgenticLogicRuleVersion } from "@src/api/agentic-logic/update-agentic-logic-rule-version";
import { RollbackConfirmModal } from "@src/features/agenticCodegen/workspace/components/modals/RollbackConfirmModal/RollbackConfirmModal";
import { VersionHistoryPanel } from "@src/features/agenticCodegen/workspace/components/VersionHistory/VersionHistoryPanel";
import type { WorkspaceDraft } from "@src/features/agenticCodegen/workspace/types";

import { agenticRollbackImpactCopy } from "./agenticDomain";
import { RuleVersionBody } from "./RuleVersionBody";
import { useRuleVersionHistory } from "./useRuleVersionHistory";

/**
 * The rule page's Versions tab: every published version of this rule, newest
 * first, with the unpublished draft on top.
 *
 * ONE home for versions, for both rule flavours. It used to be a view inside
 * the Advanced tab, which is gated on `advanced_agentic_logic_enabled` — so
 * the merchants who most need to answer "what are shoppers seeing" and "how do
 * I go back" were exactly the ones who never learned versions existed. It also
 * has a second job now: Save no longer deploys a rule that is attached to a
 * started experience (`publishOnSave.ts`), so this is where the merchant comes
 * to see whether what they saved is what is serving.
 *
 * Bound to the rule id rather than to a workspace view model, which is what
 * lets a template rule and a code rule render the identical tab: the versions
 * list, rollback and delete are rule-level facts, and only the body of an
 * expanded card differs between the two.
 */
export const RuleVersionsTab = ({
  ruleId,
  draft,
  draftContent,
  source,
  readOnly = false,
}: {
  ruleId: string;
  /**
   * Saved draft metadata. Template rules supply it from their saved rule row.
   */
  draft: WorkspaceDraft | null;
  draftContent?: ReactNode;
  /**
   * The raw per-version source view, for orgs with the Advanced toggle on.
   * `undefined` withholds it and the card shows only the graph — see
   * {@link RuleVersionBody}, which is where that call is explained.
   */
  source?: { label: string; render: (version: number) => ReactNode };
  readOnly?: boolean;
}) => {
  const history = useRuleVersionHistory(ruleId);
  const renameVersion = useUpdateAgenticLogicRuleVersion();

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <VersionHistoryPanel
        activeLabel="Published"
        versions={history.versions}
        draft={draft}
        draftContent={draftContent}
        aiAuthorName="Atlas"
        onPublishVersion={history.openRollback}
        onDeleteVersion={history.deleteVersion}
        onRenameVersion={
          readOnly
            ? undefined
            : async (version, title) => {
                try {
                  await renameVersion.mutateAsync({
                    body: { title },
                    pathParams: { ruleId, version: String(version) },
                    queryParams: undefined,
                  });
                } catch {
                  // Swallowed on purpose. A refused rename is already reported
                  // to the merchant by the mutation's own `onError` toast, and
                  // the panel drives this callback as `void commit()` — so a
                  // rejection here reaches no handler that could report it a
                  // second time, and becomes an unhandled rejection instead.
                  // The callback's job is to SETTLE when the write settles.
                }
              }
        }
        isRenamingVersion={renameVersion.isPending}
        renderVersionCode={(version) => (
          <RuleVersionBody
            ruleId={ruleId}
            version={version}
            source={source?.render(version)}
            sourceLabel={source?.label ?? "Source"}
          />
        )}
        // No documented retention ceiling for rules, so no version limit.
        versionLimit={null}
        readOnly={readOnly}
      />

      <RollbackConfirmModal
        open={history.rollbackTargetVersion !== null}
        onClose={history.closeRollback}
        onConfirm={history.confirmRollback}
        targetVersion={history.rollbackTargetVersion}
        versions={history.versions}
        isRollingBack={history.isRollingBack}
        impactCopy={agenticRollbackImpactCopy}
      />
    </Stack>
  );
};
